import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Card, CardBody } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { api, ApiException } from "../lib/api";

interface InvitePreview {
  invite: {
    email: string;
    role: "admin" | "member";
    circle_name: string;
    loved_one_name: string | null;
    inviter_name: string;
  };
}

export function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    api
      .get<InvitePreview>(`/api/invites/${encodeURIComponent(token)}`)
      .then(setPreview)
      .catch((err) => setError(err instanceof ApiException ? err.message : "Invalid invite."))
      .finally(() => setLoading(false));
  }, [token]);

  async function accept() {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await api.post<{ circle_id: string }>(`/api/invites/${encodeURIComponent(token)}/accept`);
      navigate("/app", { replace: true });
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed to accept.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full flex flex-col">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto max-w-5xl px-5 h-14 flex items-center">
          <Link to="/" className="font-semibold tracking-tight">Carol Care</Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5 py-10">
        <Card className="w-full max-w-md">
          <CardBody>
            {loading && <div className="text-text-muted">Loading invite…</div>}
            {!loading && error && (
              <>
                <h1 className="text-2xl font-semibold tracking-tight">Invite unavailable</h1>
                <p className="mt-2 text-text-muted">{error}</p>
                <div className="mt-6">
                  <Link to="/signin"><Button variant="secondary">Sign in</Button></Link>
                </div>
              </>
            )}
            {!loading && preview && (
              <>
                <h1 className="text-2xl font-semibold tracking-tight">
                  You're invited
                </h1>
                <p className="mt-2 text-text-muted">
                  <span className="font-medium text-text">{preview.invite.inviter_name}</span> added you to <span className="font-medium text-text">{preview.invite.circle_name}</span>
                  {preview.invite.loved_one_name && (
                    <> — the care circle for <span className="font-medium text-text">{preview.invite.loved_one_name}</span></>
                  )}
                  .
                </p>
                <div className="mt-4 flex items-center gap-2 text-sm">
                  <span className="text-text-muted">You'll join as</span>
                  <Badge tone={preview.invite.role === "admin" ? "info" : "neutral"}>
                    {preview.invite.role}
                  </Badge>
                </div>
                <div className="mt-6">
                  <Button size="lg" onClick={accept} loading={busy} className="w-full">
                    Accept invite
                  </Button>
                </div>
                <p className="mt-3 text-xs text-text-muted text-center">
                  We'll sign you in as <span className="font-medium">{preview.invite.email}</span> if you aren't already.
                </p>
              </>
            )}
          </CardBody>
        </Card>
      </main>
    </div>
  );
}
